import type {OnSwitchingCallback} from 'react-swipeable-views';
import type {SlideRendererCallback} from 'react-swipeable-views-utils';

import {AspectRatio, Fade} from '@travel-frontend/ui-components';
import {noop} from 'lodash';
import {useCallback, useState} from 'react';

import {ImageGalleryNextControl} from '@/components/ImageGalleryNextControl';
import {ImageGalleryPreviousControl} from '@/components/ImageGalleryPreviousControl';
import {Container} from '@/components/ImageGallerySlider/components/Container';
import {ControlContainer} from '@/components/ImageGallerySlider/components/ControlContainer';
import {Image} from '@/components/ImageGallerySlider/components/Image';
import {ImageGrid} from '@/components/ImageGallerySlider/components/ImageGrid';
import {VirtualizedSwipeableViews} from '@/components/ImageGallerySlider/components/VirtualizedSwipeableViews';
import {testId} from '@/utils/testId';

const getSlideImages = (
  slideIndex: number,
  images: Array<string>,
  imagesPerSlide: number
) => {
  const offset = slideIndex * imagesPerSlide;

  const slideImages = [];
  for (let counter = 0; counter < imagesPerSlide; counter++) {
    const index = offset + counter;
    // Note that src can be empty if the last slide contains less images than imagesPerSlide.
    // This is intentional since we want to render placeholder elements for those missing images.
    const source = images[index];

    slideImages.push({index, src: source});
  }

  return slideImages;
};

type Props = {
  images: Array<string>;
  activeImage?: number;
  onClickPrev?: () => void;
  onClickNext?: () => void;
  onClickImage?: (index: number) => void;
  // internal
  slideIndex: number;
  imagesPerSlide: number;
  onChangeSlideIndex: (index: number) => void;
};

export const ImageGallerySlider: React.FunctionComponent<Props> = ({
  images,
  activeImage,
  onClickPrev,
  onClickNext,
  onClickImage = noop,
  // internal
  slideIndex,
  imagesPerSlide,
  onChangeSlideIndex,
}) => {
  const [isSwiping, setIsSwiping] = useState(false);
  const [hovering, setHovering] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setHovering(true);
  }, []);
  const handleMouseLeave = useCallback(() => {
    setHovering(false);
  }, []);
  const handleSwitching: OnSwitchingCallback = useCallback((_, type) => {
    if (type === 'move') {
      setIsSwiping(true);
    }
  }, []);
  const handleTransitionEnd = useCallback(() => {
    setIsSwiping(false);
  }, []);
  const handleClickImage = useCallback(
    (imageIndex: number) => () => {
      if (!isSwiping) {
        onClickImage(imageIndex);
      }
    },
    [isSwiping, onClickImage]
  );

  const slideRenderer: SlideRendererCallback = useCallback(
    ({index, key}) => (
      <ImageGrid key={key}>
        {getSlideImages(index, images, imagesPerSlide).map(
          ({index: imageIndex, src}) =>
            src ? (
              <AspectRatio
                key={imageIndex}
                ratio={3 / 2}
                onClick={handleClickImage(imageIndex)}
              >
                <Image
                  alt=""
                  src={src}
                  active={imageIndex === activeImage}
                  draggable="false"
                />
              </AspectRatio>
            ) : (
              <div key={`placeholder-${imageIndex}`} />
            )
        )}
      </ImageGrid>
    ),
    [activeImage, images, imagesPerSlide, handleClickImage]
  );

  const slideCount = Math.ceil(images.length / imagesPerSlide);
  const hasSwipedBeyondStart = slideIndex > 0;
  const hasSwipedToEnd = slideIndex < slideCount - 1;

  return (
    <Container onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <VirtualizedSwipeableViews
        index={slideIndex}
        onChangeIndex={onChangeSlideIndex}
        overscanSlideBefore={1}
        overscanSlideAfter={1}
        slideCount={slideCount}
        slideRenderer={slideRenderer}
        onSwitching={handleSwitching}
        onTransitionEnd={handleTransitionEnd}
        enableMouseEvents
        resistance
        data-testid={testId('imageGalleryLayer-slider')}
      />
      <Fade in={hasSwipedBeyondStart}>
        <ControlContainer position="left">
          <ImageGalleryPreviousControl
            highlighted={hovering}
            onClick={onClickPrev}
          />
        </ControlContainer>
      </Fade>
      <Fade in={hasSwipedToEnd}>
        <ControlContainer position="right">
          <ImageGalleryNextControl
            highlighted={hovering}
            onClick={onClickNext}
          />
        </ControlContainer>
      </Fade>
    </Container>
  );
};
